import { STATISTIC_AGGREGATES } from "../constants";
import _ from "lodash";

/**
 * Pure values/amounts
 */
export function getReturnItems(statistics) {
    return statistics?.returnItems;
}

export function getReturns(statistics) {
    return statistics?.returns;
}

export function getReturnItemsByOrderDate(statistics) {
    return statistics?.returnItemsByOrderDate;
}

export function getReturnItemsTurnr(statistics) {
    if (_.isNil(statistics?.returnItemsTurnr)) {
        return getReturnItems(statistics);
    }
    return statistics?.returnItemsTurnr;
}

export function getReturnItemsTurnrByOrderDate(statistics) {
    if (_.isNil(statistics?.returnItemsTurnrByOrderDate)) {
        return getReturnItemsByOrderDate(statistics);
    }
    return statistics?.returnItemsTurnrByOrderDate;
}

export function getReturnItemsOutsideTurnr(statistics) {
    return getReturnItems(statistics) - getReturnItemsTurnr(statistics);
}

export function getReturnItemsOutsideTurnrByOrderDate(statistics) {
    return getReturnItemsByOrderDate(statistics) - getReturnItemsTurnrByOrderDate(statistics);
}

export function getExchangeItems(statistics) {
    if (_.isNil(statistics?.exchangeItems)) {
        return getExchangeItemsFrom(statistics);
    }
    return statistics?.exchangeItems;
}

export function getGiftCardsCreated(statistics) {
    return statistics?.giftCardsCreated;
}

export function getExchangeItemsFrom(statistics) {
    return statistics?.exchangeItemsFrom;
}

export function getComplaintItems(statistics) {
    return statistics?.complaintItems;
}

export function getOrders(statistics) {
    return statistics?.orders;
}

export function getOrderItems(statistics) {
    return statistics?.orderItems;
}

export function getRefundTransactions(statistics) {
    return statistics?.refundTransactions;
}

/**
 * Percentages
 */

function max100Wrapper(value, { max100 }) {
    return Math.min(value, max100 ? 1 : Infinity) || 0;
}

export function getPercentageReturnItems(statistics, { max100 } = {}) {
    return max100Wrapper((getReturnItemsByOrderDate(statistics) || getReturnItems(statistics)) / getOrderItems(statistics), {
        max100,
    });
}

export function getPercentageReturnItemsTurnr(statistics, { max100 } = {}) {
    return max100Wrapper(getReturnItemsTurnrByOrderDate(statistics) / getOrderItems(statistics), { max100 });
}

export function getPercentageReturnItemsOutsideTurnr(statistics, { max100 } = {}) {
    return max100Wrapper(getReturnItemsOutsideTurnrByOrderDate(statistics) / getOrderItems(statistics), { max100 });
}

export function getPercentageExchangeItems(statistics, { max100 } = {}) {
    return max100Wrapper(getExchangeItems(statistics) / getReturnItems(statistics), { max100 });
}

export function getPercentageExchangeItemsFrom(statistics, { max100 } = {}) {
    return max100Wrapper(getExchangeItemsFrom(statistics) / getReturnItems(statistics), { max100 });
}

export function getPercentageComplaintItems(statistics, { max100 } = {}) {
    return max100Wrapper(getComplaintItems(statistics) / getReturnItems(statistics), { max100 });
}

export function getPercentageGiftCards(statistics, { max100 } = {}) {
    return max100Wrapper(getGiftCardsCreated(statistics) / getRefundTransactions(statistics), { max100 });
}

/**
 * Worth / money
 */

export function getOrderItemsWorth(statistics) {
    return statistics?.orderItemsWorth;
}

export function getOrdersWorth(statistics) {
    return statistics?.ordersWorth || getOrderItemsWorth(statistics);
}

export function getReturnItemsWorth(statistics) {
    return statistics?.returnItemsWorth;
}

export function getRevenue(statistics) {
    return statistics?.revenue;
}

export function getExchangeItemsWorth(statistics) {
    return statistics?.exchangeItemsWorth;
}

export function getGiftCardsCreatedWorth(statistics) {
    return statistics?.giftCardsCreatedWorth;
}

export function getComplaintItemsWorth(statistics) {
    return statistics?.complaintItemsWorth;
}

export function getRefundTransactionsWorth(statistics) {
    return statistics?.refundTransactionsWorth;
}

/**
 * Statistics entity collection helpers
 */

export function getLast3MonthsStats(entity) {
    return entity?.statistics?.[STATISTIC_AGGREGATES.LAST_3_MONTHS];
}

export function getStatistics(entity, { aggregate }) {
    return entity?.statistics?.[aggregate];
}

/**
 * Smart functions when switching between percent on graphs
 */

export function getReturnItemsMaybePercent(statistics, { percentIsOn, max100 }) {
    return percentIsOn ? getPercentageReturnItems(statistics, { max100 }) : getReturnItems(statistics);
}

export function getReturnItemsTurnrMaybePercent(statistics, { percentIsOn, max100 }) {
    return percentIsOn ? getPercentageReturnItemsTurnr(statistics, { max100 }) : getReturnItemsTurnr(statistics);
}

export function getReturnItemsOutsideTurnrMaybePercent(statistics, { percentIsOn, max100 }) {
    return percentIsOn ? getPercentageReturnItemsOutsideTurnr(statistics, { max100 }) : getReturnItemsOutsideTurnr(statistics);
}

export function getExchangeItemsFromMaybePercent(statistics, { percentIsOn, max100 }) {
    return percentIsOn ? getPercentageExchangeItemsFrom(statistics, { max100 }) * getPercentageReturnItems(statistics, { max100 }) : getExchangeItemsFrom(statistics);
}

export function getExchangeItemsMaybePercent(statistics, { percentIsOn, max100 }) {
    return percentIsOn ? getPercentageExchangeItems(statistics, { max100 }) * getPercentageReturnItems(statistics, { max100 }) : getExchangeItems(statistics);
}

export function getComplaintItemsMaybePercent(statistics, { percentIsOn, max100 } = {}) {
    return percentIsOn ? getPercentageComplaintItems(statistics, { max100 }) * getPercentageReturnItems(statistics, { max100 }) : getComplaintItems(statistics);
}

/**
 * BigQuery -> legacy statistics adapter
 *
 * Maps the per-customer BigQuery statistics (customer.statisticsBq, mirrored from the
 * returns_aggregated_customer_statistics table) into the legacy `statistics` shape that the
 * getters above and the ReturnStatisticsCard / ReturnStatisticsCardAllData components consume.
 *
 * Uses the shop-currency value fields, since the cards are rendered with the brand currency.
 * Percentage fields from BigQuery are 0-1 fractions, matching what the cards expect.
 * Fields with no BigQuery equivalent (gift cards, refund transactions) are left undefined so
 * those rows simply do not render.
 */
export function mapBigQueryStatisticsToStatistics(statisticsBq) {
    if (!statisticsBq) {
        return undefined;
    }
    return {
        orders: statisticsBq.totalNrOrders,
        orderItems: statisticsBq.totalNrOrderedItems,
        returns: statisticsBq.totalNrReturns,
        returnItems: statisticsBq.totalNrReturnedItems,
        exchangeItemsFrom: statisticsBq.totalNrExchangeItems,
        complaintItems: statisticsBq.totalNrItemsWComplaint,

        ordersWorth: statisticsBq.totalOrderValueShop,
        orderItemsWorth: statisticsBq.totalOrderedItemsValueShop,
        returnItemsWorth: statisticsBq.totalReturnedItemsValueShop,
        exchangeItemsWorth: statisticsBq.totalExchangedItemsValueShop,
        complaintItemsWorth: statisticsBq.totalComplaintItemsValueShop,

        // BigQuery stores percentages in 0-100 units (e.g. 63.3 = 63.3%), but the cards expect a
        // 0-1 fraction (they multiply by 100 for display), so scale it down.
        returnRate: _.isNil(statisticsBq.percReturnedItemsPerOrderedItems) ? undefined : statisticsBq.percReturnedItemsPerOrderedItems / 100,
    };
}
